import type { paths } from '@luxuryescapes/contract-svc-lux-loyalty'

type AccountMeResult = paths['/api/lux-loyalty/account/me']['get']['responses']['200']['content']['application/json']['result']

export interface MappedLoyaltyAccountResult {
  account: App.LoyaltyAccount | undefined
  promoCampaigns: Array<App.LoyaltyPromoCampaign>
}

/** @TODO properly support 'family_and_friends_access' */
const DISABLED_BENEFIT_CODES = new Set<App.LoyaltyBenefitType>(['hidden_access', 'family_and_friends_access'])

function mapPromoCampaigns(promoCampaigns: AccountMeResult['promoCampaigns']): Array<App.LoyaltyPromoCampaign> {
  return (promoCampaigns ?? []).map((campaign): App.LoyaltyPromoCampaign => ({
    id: campaign.id,
    name: campaign.name,
    benefit: campaign.benefit,
    quantity: campaign.quantity,
    used: campaign.used,
    available: campaign.available,
    startDate: campaign.startDate,
    endDate: campaign.endDate,
  }))
}

function mapLoyaltyAccount(result: AccountMeResult): MappedLoyaltyAccountResult {
  const response = result.accountSummary
  const promoCampaigns = mapPromoCampaigns(result.promoCampaigns)

  if (!response) {
    return { account: undefined, promoCampaigns }
  }

  const enabledBenefits = response.benefits.filter(benefit => !DISABLED_BENEFIT_CODES.has(benefit.code))

  return {
    account: {
      accountNumber: response.accountNumber,
      points: response.points,
      tier: response.tier,
      statusCredits: response.statusCredits,
      ibsMemberId: response.ibsMemberId,
      statusReviewDate: response.statusReviewDate ?? undefined,
      membershipYearEndDate: response.membershipYearEndDate ?? undefined,
      totalCreditsForTierRetention: response.totalCreditsForTierRetention ?? 0,
      totalCreditsForTierUpgrade: response.totalCreditsForTierUpgrade ?? 0,
      numberOfNights: response.numberOfNights ?? 0,
      numberOfNightsForTierUpgrade: response.numberOfNightsForTierUpgrade ?? 0,
      numberOfNightsForTierRetention: response.numberOfNightsForTierRetention ?? 0,
      benefits: enabledBenefits.map((benefit): App.LoyaltyBenefit => ({
        code: benefit.code,
        description: benefit.description,
        remaining: benefit.details.remaining === 'unlimited' ? undefined : benefit.details.remaining,
        limit: benefit.details.limit === 'unlimited' ? undefined : benefit.details.limit,
        unlimited: benefit.details.remaining === 'unlimited' || benefit.details.limit === 'unlimited',
        value: benefit.details.value ? {
          amount: benefit.details.value.amount,
          unit: benefit.details.value.unit,
          currency: benefit.details.value.currency,
        } : undefined,
      })),
      promoCampaigns,
      customerServiceRep: response.customerServiceRep ?? undefined,
      region: response.region,
      createdAt: response.createdAt,
    },
    promoCampaigns,
  }
}

export default mapLoyaltyAccount
